// Open: customerStatus has no source yet, pending the backend.
// subTotal assumes US prices without tax (taxPercentage 0). Convert to ex-tax before enabling other markets.

import type { ImpactConversionProps } from '@activebrands/core-web/libs/impact/types';
import { money, toCategory, toSubTotal, truncate } from '@activebrands/core-web/libs/impact/utils';
import { logger } from '@activebrands/core-web/utils/logger';
import { sha1 } from '@activebrands/core-web/utils/sha1';
import Gtm from '@grebban/gtm';

/**
 * Pushes the Online Sale payload for Impact's trackConversion tag. Field lengths
 * follow the limits in the technical plan; unavailable values must be empty
 * strings, never null or undefined.
 *
 * customProfileId and clickid are added by GTM from its own cookie variables.
 */
export default async ({ id, items, totals, totalDiscount, currencyCode, user }: ImpactConversionProps) => {
    try {
        if (!items?.length) {
            return;
        }

        // Impact accepts a single promo code; stacked codes are not supported.
        const [promoCode = ''] = totalDiscount?.discounts?.map(discount => discount.id) ?? [];
        const totalDiscountAsNumber = Number(totals?.totalDiscountAsNumber) || 0;

        const impact = {
            orderId: truncate(String(id), 64),
            customerId: truncate(user?.contactId ?? '', 255),
            customerEmail: await sha1(user?.email ?? ''),
            customerStatus: '',
            currencyCode,
            orderPromoCode: truncate(promoCode, 64),
            orderDiscount: money(Math.abs(totalDiscountAsNumber * ((100 - totals.taxPercentage) / 100))),
            items: items.map(item => ({
                subTotal: toSubTotal(item),
                category: toCategory(item),
                sku: truncate(item.sku, 40),
                quantity: item.quantity,
                name: truncate(item.productName, 128),
            })),
        };

        Gtm.addDataLayerEvent('impact_transaction', { impact });
    } catch (error) {
        logger.error(error);
    }
};
